import { useEffect, useRef, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { Search, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Auto-suggest input for Existing Client selection.
 * Fetches /api/clients/search which is hierarchy-scoped server-side.
 */
export function ClientSearchInput({ value, onSelect, testid = "client-search", placeholder = "Search client code or name…" }) {
  const [q, setQ] = useState(value?.label || "");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  const load = useCallback(async (query) => {
    if (!query || query.trim().length < 1) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/clients/search", { params: { q: query, limit: 12 } });
      setRows(data);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 200);
    return () => clearTimeout(t);
  }, [q, load]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (row) => {
    onSelect({
      id: row.id,
      client_code: row.client_code,
      client_name: row.client_name,
      label: `${row.client_code} · ${row.client_name}`,
    });
    setQ(`${row.client_code} · ${row.client_name}`);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(rows.length - 1, h + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    if (e.key === "Enter" && rows[highlight]) { e.preventDefault(); pick(rows[highlight]); }
    if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid={testid}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHighlight(0); if (value) onSelect(null); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="pl-9 pr-8"
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
        {value && !loading && <Check className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--success))]" />}
      </div>
      {open && (rows.length > 0 || (q && !loading)) && (
        <div
          data-testid={`${testid}-panel`}
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-popover shadow-lg"
        >
          {rows.length === 0 ? (
            <div className="p-3 text-xs text-muted-foreground">
              No matching clients in your hierarchy. Choose &ldquo;New Client&rdquo; to add one.
            </div>
          ) : (
            rows.map((r, i) => (
              <button
                key={r.id}
                type="button"
                data-testid={`${testid}-option-${r.client_code}`}
                onClick={() => pick(r)}
                onMouseEnter={() => setHighlight(i)}
                className={cn(
                  "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm",
                  i === highlight ? "bg-muted" : "hover:bg-muted",
                )}
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{r.client_name}</div>
                  <div className="truncate font-mono text-[11px] text-muted-foreground">{r.client_code}</div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
